import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { ChartCard } from "@/components/v2/ChartCard";
import { EmptyState } from "@/components/v2/EmptyState";
import { MarketTable } from "@/components/v2/MarketTable";
import { SectionBlock } from "@/components/v2/SectionBlock";
import { StatGridCard } from "@/components/v2/StatGridCard";
import { LineMetricChart } from "@/components/v2/charts";
import { useProviderHistory, useProviderSummary } from "@/hooks/useHip3Analytics";
import { useNormalizedMarkets } from "@/hooks/useNormalizedMarkets";
import { formatCompact, formatPercent, formatSpreadBps } from "@/lib/formatters";
import { marketNotionalOi, normalizeProvider } from "@/lib/market-utils";

function normalizeHistoryPoints(
  points: Array<{ t?: string; value?: number | null }>
): Array<{ t: string; value: number | null }> {
  return points.reduce<Array<{ t: string; value: number | null }>>((acc, point) => {
    if (typeof point.t !== "string" || !point.t) return acc;
    const value =
      typeof point.value === "number" && Number.isFinite(point.value) ? point.value : null;
    acc.push({ t: point.t, value });
    return acc;
  }, []);
}

export function ProviderDetailPage() {
  const params = useParams();
  const providerParam = normalizeProvider(params.provider ?? "");

  const marketsQuery = useNormalizedMarkets();
  const summaryQuery = useProviderSummary({ window: "7d", sort: "oiShare" });
  const historyQuery = useProviderHistory({ metric: "oi", window: "30d", interval: "4h" });

  const providerMarkets = useMemo(
    () => marketsQuery.markets.filter((market) => normalizeProvider(market.provider) === providerParam),
    [marketsQuery.markets, providerParam]
  );

  const providerSummary = useMemo(
    () =>
      summaryQuery.data?.providers.find(
        (provider) => normalizeProvider(provider.provider) === providerParam
      ),
    [summaryQuery.data?.providers, providerParam]
  );

  const providerHistorySeries = useMemo(
    () => {
      const points =
        historyQuery.data?.series.find(
          (series) => normalizeProvider(series.provider) === providerParam
        )?.points ?? [];
      return normalizeHistoryPoints(points);
    },
    [historyQuery.data?.series, providerParam]
  );

  const providerStats = useMemo(() => {
    const marketCount = providerMarkets.length;
    const assets = new Set(providerMarkets.map((market) => market.baseAsset)).size;
    const oiUsd = providerMarkets.reduce((sum, market) => sum + marketNotionalOi(market), 0);
    const spreadValues = providerMarkets
      .map((market) => market.spreadBps)
      .filter((value): value is number => typeof value === "number" && Number.isFinite(value));

    const spreadAvg =
      spreadValues.length > 0
        ? spreadValues.reduce((sum, value) => sum + value, 0) / spreadValues.length
        : null;

    return {
      marketCount,
      assets,
      oiUsd,
      spreadAvg,
    };
  }, [providerMarkets]);

  if (!providerParam) {
    return <EmptyState title="Provider not found" description="Missing provider identifier." />;
  }

  return (
    <div className="space-y-6">
      <SectionBlock
        title={`${providerParam.toUpperCase()} Provider`}
        subtitle="Provider footprint, growth, and full market list."
      >
        <div className="grid gap-3 md:grid-cols-5">
          <StatGridCard
            label="Markets"
            value={String(providerStats.marketCount)}
            sub="Provider markets"
          />
          <StatGridCard label="Assets" value={String(providerStats.assets)} sub="Unique assets" />
          <StatGridCard
            label="OI Share"
            value={formatPercent((providerSummary?.oiShare ?? 0) * 100)}
            sub="Of ecosystem OI"
          />
          <StatGridCard
            label="Volume Share"
            value={formatPercent((providerSummary?.volumeShare ?? 0) * 100)}
            sub="Of ecosystem volume"
          />
          <StatGridCard
            label="Avg Spread"
            value={formatSpreadBps(providerSummary?.spreadAvgBps ?? providerStats.spreadAvg)}
            sub={`OI ${formatCompact(providerStats.oiUsd, "$")}`}
          />
        </div>
      </SectionBlock>

      <SectionBlock title="Provider Growth" subtitle="Open interest growth over the past 30 days.">
        <ChartCard title="OI Over Time" loading={historyQuery.isLoading}>
          <LineMetricChart
            data={providerHistorySeries}
            valueFormatter={(value) => formatCompact(value, "$")}
          />
        </ChartCard>
      </SectionBlock>

      <SectionBlock
        title="Provider Markets"
        subtitle="All markets currently listed by this provider."
      >
        <MarketTable markets={providerMarkets} />
      </SectionBlock>
    </div>
  );
}
